
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase, handleSupabaseError } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

type AuthContextType = {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isLoading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isAdmin: false,
  isLoading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // First set up the auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log("Auth state changed:", event, "User ID:", currentSession?.user?.id);
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        // We need to check admin status but must avoid calling Supabase directly in this callback
        // to prevent potential deadlocks. Use setTimeout to defer the call.
        if (currentSession?.user) {
          setTimeout(() => {
            checkIfUserIsAdmin(currentSession.user.id);
            // Ensure legacy users receive a paddle number if missing
            ensurePaddleAssigned(currentSession.user.id);
          }, 0);
        } else {
          setIsAdmin(false);
        }
      }
    );

    // Then check for an existing session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      console.log("Initial session check:", currentSession?.user?.id);
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      
      if (currentSession?.user) {
        checkIfUserIsAdmin(currentSession.user.id);
        ensurePaddleAssigned(currentSession.user.id);
      } else {
        console.log("No active session found");
      }
      
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  async function checkIfUserIsAdmin(userId: string) {
    try {
      console.log("Checking admin status for user:", userId);
      
      // Apply the hardcoded email check as a fast path for development
      if (user?.email === "vaynerov@icloud.com") {
        console.log("Admin email match detected");
        setIsAdmin(true);
        return;
      }
      
      // Use the security definer function we created in the database
      // This avoids the recursive RLS policy issue
      const { data, error } = await supabase.rpc('is_admin', { user_id: userId });
      
      if (error) {
        console.error("Error checking admin status via RPC:", error);
        return;
      }

      console.log("Admin status check result:", data);
      setIsAdmin(!!data);
    } catch (error: any) {
      const errorMessage = handleSupabaseError(error);
      console.error("Error checking admin status:", errorMessage);
    }
  }

  async function signOut() {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      toast({
        title: "Signed out",
        description: "You have been successfully signed out"
      });
    } catch (error: any) {
      const errorMessage = handleSupabaseError(error);
      console.error("Error signing out:", error);
      toast({
        title: "Error",
        description: "Failed to sign out: " + errorMessage,
        variant: "destructive"
      });
    }
  }

  async function ensurePaddleAssigned(userId: string) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('paddle_number')
        .eq('id', userId as any)
        .single();
      if (error) return;
      if (!data || !(data as any).paddle_number) {
        await supabase.rpc('assign_paddle_number', { user_id: userId });
      }
    } catch (e) {
      console.warn('Paddle assignment check failed:', e);
    }
  }

  return (
    <AuthContext.Provider value={{ user, session, isAdmin, isLoading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
